import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  ChevronRight,
  PhoneIphone,
  Search,
  Storefront,
} from '@mui/icons-material';
import apiService from '../services/apiService';
import StudioSelector, { StudioOption } from '../components/StudioSelector';
import { colors } from '../theme/theme';

interface Brand {
  _id: string;
  name: string;
  slug: string;
  appSource?: string;
  logo?: string;
  status: 'active' | 'suspended';
  studioCount: number;
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  BRANDS — the businesses, not the addresses
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Replaces the old Branches tab, which could only exist on a studio flagged as
 * a "branch parent". That flag made a brand's identity a trading location:
 * whichever studio happened to hold it WAS the brand, so deactivating that shop
 * orphaned every other one.
 *
 * A table rather than a grid of cards. A brand row carries five facts and no
 * picture worth looking at, and an operator scans this list to find one name
 * among dozens — which a card grid is actively bad at.
 */
const BrandsPage: React.FC = () => {
  const navigate = useNavigate();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'multi' | 'app'>('all');

  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', appSource: '', supportEmail: '' });
  const [adopt, setAdopt] = useState<StudioOption | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res: any = await apiService.getBrands(search ? { search } : undefined);
      setBrands(res?.brands ?? []);
    } catch (e: any) {
      setError(e.message || 'Could not load brands');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const stats = useMemo(() => {
    const locations = brands.reduce((n, b) => n + (b.studioCount || 0), 0);
    return {
      brands: brands.length,
      locations,
      multi: brands.filter((b) => (b.studioCount || 0) > 1).length,
      apps: brands.filter((b) => !!b.appSource).length,
    };
  }, [brands]);

  const rows = useMemo(() => {
    if (filter === 'multi') return brands.filter((b) => (b.studioCount || 0) > 1);
    if (filter === 'app') return brands.filter((b) => !!b.appSource);
    return brands;
  }, [brands, filter]);

  const create = async () => {
    setSaving(true);
    setError(null);
    try {
      const res: any = await apiService.createBrand({
        name: form.name.trim(),
        appSource: form.appSource.trim() || undefined,
        supportEmail: form.supportEmail.trim() || undefined,
        adoptStudio: adopt?._id,
      });
      setCreateOpen(false);
      setForm({ name: '', appSource: '', supportEmail: '' });
      setAdopt(null);
      navigate(`/brands/${res.brand._id}`);
    } catch (e: any) {
      setError(e.message || 'Could not create the brand');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'flex-start' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: '-0.5px' }}>
            Brands
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {loading
              ? 'Loading…'
              : `${stats.brands} brands · ${stats.locations} locations · ${stats.multi} with more than one`}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateOpen(true)}
          sx={{ alignSelf: { md: 'flex-start' }, flexShrink: 0 }}
        >
          New brand
        </Button>
      </Stack>

      {/*
        The counts live in the subtitle rather than in four large tiles. Big-number
        tiles earn their space when those figures are the point of the page; here the
        point is the LIST, and the tiles pushed it below the fold for numbers nobody
        acts on.
      */}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Controls ──────────────────────────────────────────────────────── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 2 }}
        alignItems={{ sm: 'center' }}
      >
        <TextField
          size="small"
          placeholder="Search brands"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ maxWidth: 320, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          onChange={(_, v) => v && setFilter(v)}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="multi">Multi-location</ToggleButton>
          <ToggleButton value="app">Has an app</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* ── List ──────────────────────────────────────────────────────────── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper variant="outlined" sx={{ textAlign: 'center', py: 8, borderRadius: 2 }}>
          <Storefront sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            {search ? `No brand matches “${search}”.` : 'No brands match this filter.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: colors.backgroundTertiary }}>
                <TableCell sx={{ fontWeight: 700 }}>Brand</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Locations</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>App</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell width={48} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((b) => (
                <TableRow
                  key={b._id}
                  hover
                  onClick={() => navigate(`/brands/${b._id}`)}
                  sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        src={b.logo}
                        variant="rounded"
                        sx={{
                          width: 38,
                          height: 38,
                          bgcolor: colors.primary,
                          color: '#fff',
                          fontSize: 14,
                          fontWeight: 700,
                        }}
                      >
                        {initials(b.name)}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={600} noWrap>
                          {b.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          /{b.slug}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    {b.studioCount > 1 ? (
                      <Chip size="small" color="primary" label={`${b.studioCount} locations`} />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {b.studioCount === 1 ? 'Single location' : '—'}
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    {b.appSource ? (
                      <Tooltip title="x-app-source its apps send">
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<PhoneIphone sx={{ fontSize: '14px !important' }} />}
                          label={b.appSource}
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        —
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      label={b.status === 'active' ? 'Active' : 'Suspended'}
                      color={b.status === 'active' ? 'success' : 'warning'}
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell>
                    <ChevronRight sx={{ color: 'text.disabled' }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Create ────────────────────────────────────────────────────────── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 0.5 }}>New brand</DialogTitle>
        <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 1 }}>
          A brand can start with one location and grow. You do not need to know
          the branches up front.
        </Typography>
        <Divider />
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Brand name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              autoFocus
              placeholder="Chakra Athletica"
            />
            <TextField
              label="App source"
              value={form.appSource}
              onChange={(e) => setForm({ ...form, appSource: e.target.value })}
              fullWidth
              placeholder="chakra"
              helperText="The x-app-source its apps send. Every location of the brand answers to it. Leave blank if the brand has no app of its own."
            />
            <TextField
              label="Support email"
              value={form.supportEmail}
              onChange={(e) => setForm({ ...form, supportEmail: e.target.value })}
              fullWidth
              helperText="Where a location with no login of its own gets emailed."
            />
            <Box>
              <StudioSelector
                value={adopt}
                onChange={setAdopt}
                label="Start from an existing studio (optional)"
                minWidth={0}
              />
              <Typography variant="caption" color="text.secondary">
                Makes that studio the brand&rsquo;s first location. A studio that is
                already a branch of another brand is refused &mdash; move it
                deliberately instead.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={create} disabled={saving || !form.name.trim()}>
            {saving ? 'Creating…' : 'Create brand'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BrandsPage;
